import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { partnerGroupChannelQueryKeys } from '../api/use-partner-group-channel-query';
import { buildPartnerGroupChannelMessageMapOptions } from '../components/Partner/Community/mapPartnerGroupChannel';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationProvider';
import { useSocket } from '../contexts/SocketProvider';
import { handleForegroundPushMessage } from '../services/fcmPushDisplay';
import { useSocketEvent } from './useSocketEvent';
import {
  getPartnerGroupChatSenderUid,
  getPartnerGroupChatSocketChannelId,
  mapPartnerGroupChatSocketToChatMessage,
  mapPartnerGroupChatSocketToPushPayload,
  mapPartnerGroupChatSocketToReactionUpdate,
  shouldSkipOwnReactionSocketEvent,
} from '../utils/partnerGroupChatSocketPayload';
import {
  appendPartnerGroupChatMessageToCache,
  PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
  updatePartnerGroupChatMessageReactionsInCache,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import {
  doesSocketChannelMatchLiveCommunityView,
  getPartnerCommunityLiveView,
} from '../components/Partner/Community/partnerCommunityLiveView';

/**
 * Real-time partner community chat messages for partner org users.
 * Backend delivers to room user:{partnerDbUserId}, excluding the sender.
 */
export const usePartnerGroupChatSocketListener = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { isPartner, firebaseUid, partnerProfile, incrementNotificationCount } = useAuth();
  const { isConnected } = useSocket();
  const { openNotification } = useNotificationContext() ?? {};

  const messageMapOptions = useMemo(
    () => buildPartnerGroupChannelMessageMapOptions({ firebaseUid, partnerProfile }),
    [firebaseUid, partnerProfile],
  );

  const handlePartnerGroupChatMessage = useCallback(
    (envelope) => {
      if (!isPartner) return;

      const senderUid = getPartnerGroupChatSenderUid(envelope);
      if (senderUid && firebaseUid && senderUid === firebaseUid) {
        return;
      }

      const socketChannelId = getPartnerGroupChatSocketChannelId(envelope);
      const chatMessage = mapPartnerGroupChatSocketToChatMessage(envelope, messageMapOptions);
      const isViewingSameCommunityChat =
        doesSocketChannelMatchLiveCommunityView(socketChannelId) && Boolean(chatMessage);

      if (isViewingSameCommunityChat) {
        appendPartnerGroupChatMessageToCache(queryClient, chatMessage, {
          limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
          channelId: getPartnerCommunityLiveView().streamChannelId,
        });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
      });

      const pushPayload = mapPartnerGroupChatSocketToPushPayload(envelope);

      void handleForegroundPushMessage(pushPayload, {
        onOpen: openNotification,
        onNewNotification: incrementNotificationCount,
      });
    },
    [
      firebaseUid,
      incrementNotificationCount,
      isPartner,
      messageMapOptions,
      openNotification,
      queryClient,
    ],
  );

  useSocketEvent(
    SOCKET_EVENTS.PARTNER_GROUP_CHAT_MESSAGE,
    enabled && isPartner && isConnected ? handlePartnerGroupChatMessage : null,
    [enabled, isPartner, isConnected, handlePartnerGroupChatMessage],
  );

  const handlePartnerGroupChatReaction = useCallback(
    (envelope) => {
      if (!isPartner) return;

      if (shouldSkipOwnReactionSocketEvent(envelope, firebaseUid)) {
        return;
      }

      const socketChannelId = getPartnerGroupChatSocketChannelId(envelope);
      if (!doesSocketChannelMatchLiveCommunityView(socketChannelId)) {
        queryClient.invalidateQueries({
          queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
        });
        return;
      }

      const reactionUpdate = mapPartnerGroupChatSocketToReactionUpdate(envelope, messageMapOptions);
      if (!reactionUpdate?.messageId) {
        return;
      }

      const { messageId, ...reactionFields } = reactionUpdate;
      updatePartnerGroupChatMessageReactionsInCache(queryClient, messageId, reactionFields);
    },
    [firebaseUid, isPartner, messageMapOptions, queryClient],
  );

  useSocketEvent(
    SOCKET_EVENTS.PARTNER_GROUP_CHAT_REACTION,
    enabled && isPartner && isConnected ? handlePartnerGroupChatReaction : null,
    [enabled, isPartner, isConnected, handlePartnerGroupChatReaction],
  );
};

export default usePartnerGroupChatSocketListener;
